/**
 * @file menu-tree.ts
 * @description Перестановки дерева меню в кабинете (phase19.16 Р1; SB15 —
 * два уровня; контракт §2 `PUT /admin/site-view/menus/order` — полный
 * порядок одного места `[{ id, parentId }]`, порядок массива = порядок
 * соседей).
 *
 * Чистые функции: перетаскивание, «Выше», «Ниже», «Вложить в пункт выше»,
 * «Вынести на верхний уровень» дают новый порядок, который уходит на
 * сервер. Третьего уровня не бывает: вложить можно только пункт без своих
 * вложенных и только в пункт, который может иметь детей.
 */

import type { SiteMenuLinkType } from "@chaiberi/shared";

/** Минимум, нужный перестановкам. */
export interface MenuTreeNode {
  id: string;
  linkType: SiteMenuLinkType;
  children: MenuTreeNode[];
}

/** Строка тела `PUT /menus/order`. */
export interface MenuOrderItem {
  id: string;
  parentId: string | null;
}

/** Типы, у которых не бывает своих вложенных пунктов (автосписок собирается сам). */
const LEAF_ONLY: ReadonlySet<SiteMenuLinkType> = new Set(["CATALOG_CATEGORIES"]);
/** Типы, которые бывают только наверху (группа-колонка и автосписок). */
const TOP_ONLY: ReadonlySet<SiteMenuLinkType> = new Set(["GROUP", "CATALOG_CATEGORIES"]);

/** Дерево → порядок для сервера (родители, затем их дети, по порядку). */
export function toOrder<T extends MenuTreeNode>(nodes: readonly T[]): MenuOrderItem[] {
  const out: MenuOrderItem[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, parentId: null });
    for (const child of node.children) out.push({ id: child.id, parentId: node.id });
  }
  return out;
}

/** Где пункт: индекс родителя (`-1` — наверху) и индекс среди соседей. */
export function locate<T extends MenuTreeNode>(nodes: readonly T[], id: string): { parent: number; index: number } | null {
  const top = nodes.findIndex((n) => n.id === id);
  if (top >= 0) return { parent: -1, index: top };
  for (let p = 0; p < nodes.length; p += 1) {
    const index = nodes[p]!.children.findIndex((c) => c.id === id);
    if (index >= 0) return { parent: p, index };
  }
  return null;
}

/** Копия дерева на два уровня (исходное не мутирует). */
function clone<T extends MenuTreeNode>(nodes: readonly T[]): T[] {
  return nodes.map((n) => ({ ...n, children: [...n.children] }));
}

/** Переставить пункт среди соседей с `from` на `to` (перетаскивание). */
export function moveSibling<T extends MenuTreeNode>(nodes: readonly T[], id: string, to: number): T[] | null {
  const at = locate(nodes, id);
  if (!at) return null;
  const tree = clone(nodes);
  const list: MenuTreeNode[] = at.parent < 0 ? tree : tree[at.parent]!.children;
  if (to < 0 || to >= list.length || to === at.index) return null;
  const [item] = list.splice(at.index, 1);
  list.splice(to, 0, item!);
  return tree;
}

/** «Выше» / «Ниже» — на одно место среди соседей. */
export function moveBy<T extends MenuTreeNode>(nodes: readonly T[], id: string, delta: -1 | 1): T[] | null {
  const at = locate(nodes, id);
  return at ? moveSibling(nodes, id, at.index + delta) : null;
}

/** Можно ли вложить пункт в пункт выше (текст причины или `null`). */
export function indentBlocker<T extends MenuTreeNode>(nodes: readonly T[], id: string): string | null {
  const at = locate(nodes, id);
  if (!at || at.parent >= 0) return "Пункт уже вложен";
  if (at.index === 0) return "Выше нет пункта, в который можно вложить";
  const node = nodes[at.index]!;
  const target = nodes[at.index - 1]!;
  if (node.children.length > 0) return "Пункт с вложенными нельзя вложить: в меню не больше двух уровней.";
  if (TOP_ONLY.has(node.linkType)) return "Колонка и список категорий — только на верхнем уровне.";
  if (LEAF_ONLY.has(target.linkType)) return "В список категорий пункты собираются сами — вложить нельзя.";
  return null;
}

/** «Вложить в пункт выше» — последним ребёнком предыдущего соседа. */
export function indent<T extends MenuTreeNode>(nodes: readonly T[], id: string): T[] | null {
  if (indentBlocker(nodes, id) !== null) return null;
  const at = locate(nodes, id)!;
  const tree = clone(nodes);
  const [item] = tree.splice(at.index, 1);
  tree[at.index - 1]!.children.push(item!);
  return tree;
}

/** «Вынести на верхний уровень» — сразу после бывшего родителя. */
export function outdent<T extends MenuTreeNode>(nodes: readonly T[], id: string): T[] | null {
  const at = locate(nodes, id);
  if (!at || at.parent < 0) return null;
  const tree = clone(nodes);
  const [item] = tree[at.parent]!.children.splice(at.index, 1);
  tree.splice(at.parent + 1, 0, item as T);
  return tree;
}

/** Сколько всего пунктов в месте (для «Шапка 6»). */
export function countItems(nodes: readonly MenuTreeNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + n.children.length, 0);
}

/**
 * «Перенести в колонку» подвала (phase19.16, приёмка Р2а; SB113, П3):
 * пункт уходит в колонку `columnId` на место `index` (по умолчанию — в
 * конец). Колонка — группа верхнего уровня; в автосписок категорий пункты
 * не переносятся (он собирается сам). Одиночный пункт верхнего уровня
 * (подвал до data-миграции П3) тоже можно убрать в колонку, если он сам не
 * колонка и без вложенных. `null` — переносить некуда или нечего.
 */
export function moveToColumn<T extends MenuTreeNode>(
  nodes: readonly T[],
  id: string,
  columnId: string,
  index?: number,
): T[] | null {
  const at = locate(nodes, id);
  const column = nodes.find((n) => n.id === columnId);
  if (!at || !column || column.id === id || LEAF_ONLY.has(column.linkType)) return null;
  if (at.parent < 0) {
    const node = nodes[at.index]!;
    if (TOP_ONLY.has(node.linkType) || node.children.length > 0) return null;
  }
  const sameColumn = at.parent >= 0 && nodes[at.parent]!.id === columnId;
  const tree = clone(nodes);
  const source: MenuTreeNode[] = at.parent < 0 ? tree : tree[at.parent]!.children;
  const [item] = source.splice(at.index, 1);
  const target = tree.find((n) => n.id === columnId)!.children;
  const to = index === undefined ? target.length : Math.max(0, Math.min(index, target.length));
  if (sameColumn && to === at.index) return null;
  target.splice(to, 0, item!);
  return tree;
}
